import Loader from '@components/framework/loader/Loader';
import {State} from '@shared/store/reducers/Index';
import {useSnackbar} from 'notistack';
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import TicketingSystemGrid from '../../TicketingSystem/TicketingSystemGrid/TicketingSystemGrid';
import {getClinicUsersList} from '@shared/services/ClinicServices';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {
  MESSAGES_EVENT_TYPE_MAPPER,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {IWorkFlow} from '@shared/services/service-mappers/WorkflowMapper';
import {getWorkflowDetails} from '@shared/services/WorkflowServices';

const ReferralsTicketsPatientDetailsDrawer = (): JSX.Element => {
  const {groupId, clinicId, patientDetails} = useSelector(
    (state: State) => state.patientDetailsDrawer
  );
  const [referralsTicketsWorkflow, setReferralsTicketsWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchReferralsWorkflow = async () => {
      try {
        setReferralsTicketsWorkflow(p => ({
          ...p,
          isLoading: true
        }));
        const response = await getWorkflowDetails({
          clinicGroupId: groupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
                'REFERRALS_TICKETS'
              ]
          }
        });
        if (response.data.workflow) {
          setReferralsTicketsWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setReferralsTicketsWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setReferralsTicketsWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (groupId) {
      fetchReferralsWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  const user = useSelector((state: State) => state.auth.user);
  const [commonState, setCommonState] = React.useState({
    isLoading: true,
    allClinicUsers: [] as UserModelMapper[]
  });

  const {enqueueSnackbar} = useSnackbar();

  const [ticketDetailsView, setTicketDetailsView] = useState<{
    show: boolean;
    ticketId?: number | null;
  }>({
    show: false
  });

  const updateTicketDetailsView = ({
    show,
    ticketId
  }: {
    show: boolean;
    ticketId?: number | null;
  }) => {
    setTicketDetailsView({show, ticketId});
  };

  useEffect(() => {
    const init = async () => {
      setCommonState(prev => ({
        ...prev,
        isLoading: true
      }));
      try {
        const response = await getClinicUsersList(groupId, clinicId);

        if (response.data) {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: response.data
          }));
        } else {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: []
          }));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      } finally {
        setCommonState(prev => ({
          ...prev,
          isLoading: false
        }));
      }
    };
    if (groupId && clinicId) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, groupId]);
  const ticketFilters = useMemo(() => ({}), []);

  if (commonState.isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }
  return (
    <div className={`prior-auth-patient-drawer`}>
      {referralsTicketsWorkflow.isLoading && <Loader />}
      {!referralsTicketsWorkflow.isLoading && referralsTicketsWorkflow.data && (
        <TicketingSystemGrid
          module={'REFERRALS_TICKETS'}
          groupPermission={'REFERRALS_TICKETS_ENABLE'}
          clinicGroupId={groupId}
          clinicId={clinicId}
          statuses={referralsTicketsWorkflow.data.status}
          allClinicUsers={commonState.allClinicUsers}
          workflowId={referralsTicketsWorkflow.data.id}
          ticketFilters={ticketFilters}
          patientId={patientDetails.patient_id}
          userId={user.id}
          showPatientInfoInGridAndTicketDetails={false}
          messageEventType={MESSAGES_EVENT_TYPE_MAPPER.PRIOR_AUTHORIZATION}
          ticketDetailsView={ticketDetailsView}
          updateTicketDetailsView={updateTicketDetailsView}
        />
      )}
    </div>
  );
};

export default ReferralsTicketsPatientDetailsDrawer;
